import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import logo from "../assets/logo_stradafacendo_cigliantours.png";
import "./Navbar.css";
import { categories } from "../utils/utils";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [travelsDropdownOpen, setTravelsDropdownOpen] = useState(false);
  const travelsDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        travelsDropdownRef.current &&
        !travelsDropdownRef.current.contains(event.target as Node)
      ) {
        setTravelsDropdownOpen(false);
      }
    }
    if (travelsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [travelsDropdownOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
    setMobileMenuOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="navbar-container">
        <div className="navbar-brand" onClick={() => handleNavigate("/")}>
          <img src={logo} alt="StradaFacendo.club" className="navbar-logo" />
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="material-icons">
            {mobileMenuOpen ? "close" : "menu"}
          </span>
        </button>

        <div className={`navbar-links ${mobileMenuOpen ? "mobile-open" : ""}`}>
          <div className="nav-main-links">
            <button
              className={`navbar-link ${
                location.pathname === "/" ? "active" : ""
              }`}
              onClick={() => handleNavigate("/")}
            >
              {t("navbar.home")}
            </button>
            <button
              className={`navbar-link ${
                location.pathname === "/about" ? "active" : ""
              }`}
              onClick={() => handleNavigate("/about")}
            >
              {t("navbar.whoWeAre")}
            </button>
            <div
              className={`navbar-link navbar-link-dropdown ${travelsDropdownOpen ? "dropdown-open" : ""}`}
              onMouseEnter={() => setTravelsDropdownOpen(true)}
              onMouseLeave={() => setTravelsDropdownOpen(false)}
              ref={travelsDropdownRef}
            >
              <span
                className={location.pathname === "/catalog" ? "active" : ""}
                onClick={(e) => {
                  // On mobile, toggle dropdown; on desktop, navigate
                  if (window.innerWidth <= 1024) {
                    e.stopPropagation();
                    setTravelsDropdownOpen(!travelsDropdownOpen);
                  } else {
                    handleNavigate("/catalog");
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {t("navbar.ourTravels")}
              </span>
              <div
                className={`travels-dropdown-menu${travelsDropdownOpen ? " open" : ""}`}
              >
                <button
                  className="dropdown-category-link"
                  onClick={() => {
                    setTravelsDropdownOpen(false);
                    navigate("/catalog");
                    setMobileMenuOpen(false);
                  }}
                >
                  {t("allCategories")}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    className="dropdown-category-link"
                    onClick={() => {
                      setTravelsDropdownOpen(false);
                      const params = new URLSearchParams();
                      if (cat.duration) params.set("duration", cat.duration);
                      if (cat.category) params.set("category", cat.category);
                      navigate(
                        `/catalog${params.toString() ? `?${params.toString()}` : ""}`,
                      );
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t(`categories.${cat.key}`)}
                  </button>
                ))}
              </div>
            </div>
            <button
              className={`navbar-link ${
                location.pathname === "/join-club" ? "active" : ""
              }`}
              onClick={() => handleNavigate("/join-club")}
            >
              {t("navbar.joinClub")}
            </button>
            <button
              className={`navbar-link ${
                location.pathname === "/contact" ? "active" : ""
              }`}
              onClick={() => handleNavigate("/contact")}
            >
              {t("navbar.contacts")}
            </button>
            {isAuthenticated && isAdmin && (
              <button
                className={`navbar-link ${
                  location.pathname === "/admin" ? "active" : ""
                }`}
                onClick={() => handleNavigate("/admin")}
              >
                {t("navbar.admin")}
              </button>
            )}
          </div>

          <div className="navbar-auth-mobile">
            {isAuthenticated ? (
              <button className="navbar-link logout-btn" onClick={handleLogout}>
                <span className="material-icons">logout</span>
                {t("navbar.logout")}
              </button>
            ) : (
              <button
                className="navbar-link login-btn"
                onClick={() => handleNavigate("/login")}
              >
                <span className="material-icons">login</span>
                {t("navbar.login")}
              </button>
            )}
          </div>

          <div className="navbar-mobile-contact">
            <span className="navbar-mobile-contact-label">
              {t("navbar.contactLabel")}
            </span>
            <a
              className="navbar-mobile-contact-number"
              href="https://wa.me/393756041897"
              target="_blank"
              rel="noopener noreferrer"
            >
              +39 375 604 1897
            </a>
            <a
              className="navbar-mobile-cta-btn"
              href="https://wa.me/393756041897"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("navbar.contactUs")}
            </a>
            <div className="navbar-mobile-social">
              <a
                href="https://www.facebook.com/stradafacendobycigliantours/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <span className="material-icons">facebook</span>
              </a>
              <a
                href="https://www.instagram.com/stradafacendobycigliantours/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="navbar-right desktop-only">
          <div className="navbar-contact">
            <span className="navbar-contact-label">
              {t("navbar.contactLabel")}
            </span>
            <div className="navbar-contact-number">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19.785"
                height="19.785"
                viewBox="0 0 19.785 19.785"
              >
                <path
                  d="M709.89,360a9.886,9.886,0,0,0-8.006,15.691l-1.232,3.676,3.8-1.215A9.891,9.891,0,1,0,709.9,360Zm-2.762,5.025c-.192-.459-.337-.477-.628-.488q-.166-.011-.331-.011a1.435,1.435,0,0,0-1.012.354,3.159,3.159,0,0,0-1.012,2.408,5.653,5.653,0,0,0,1.174,2.984,12.385,12.385,0,0,0,4.924,4.35c2.273.942,2.948.855,3.465.744a2.787,2.787,0,0,0,1.942-1.4,2.456,2.456,0,0,0,.169-1.373c-.07-.122-.262-.192-.552-.337s-1.7-.843-1.971-.936a.552.552,0,0,0-.709.215,12.075,12.075,0,0,1-.773,1.023.624.624,0,0,1-.7.11,7.288,7.288,0,0,1-2.32-1.43,8.8,8.8,0,0,1-1.6-1.995c-.169-.291-.017-.459.116-.616.146-.181.285-.308.43-.477a1.756,1.756,0,0,0,.32-.453.591.591,0,0,0-.041-.536c-.069-.145-.651-1.564-.889-2.14Z"
                  transform="translate(-700 -360)"
                  fill="#67c15e"
                  fillRule="evenodd"
                ></path>
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19.785"
                height="19.785"
                viewBox="0 0 19.785 19.785"
              >
                <path
                  d="M607.893-810a9.892,9.892,0,0,1,9.893,9.893,9.892,9.892,0,0,1-9.893,9.893A9.892,9.892,0,0,1,598-800.107,9.892,9.892,0,0,1,607.893-810Zm.592,4.029a5.457,5.457,0,0,1,1.813.537,4.93,4.93,0,0,1,1.459,1.052,4.735,4.735,0,0,1,1,1.369,6.54,6.54,0,0,1,.635,2.677c.014.342,0,.418-.074.516a.363.363,0,0,1-.587-.055.955.955,0,0,1-.057-.4,7.086,7.086,0,0,0-.107-1.016,4.673,4.673,0,0,0-1.815-3.014,4.76,4.76,0,0,0-2.749-.97c-.372-.022-.436-.035-.52-.1a.382.382,0,0,1-.014-.547c.092-.084.156-.1.475-.086.166.006.411.026.544.041Zm-4.47.211a1.329,1.329,0,0,1,.235.117,9.47,9.47,0,0,1,1.744,2.228,1.243,1.243,0,0,1,.2.863c-.063.223-.166.34-.63.713a3.409,3.409,0,0,0-.387.345.909.909,0,0,0-.128.441,3.28,3.28,0,0,0,.491,1.372,5.88,5.88,0,0,0,.982,1.154,5.418,5.418,0,0,0,1.289.91c.573.285.923.358,1.179.238a.938.938,0,0,0,.154-.086c.019-.017.17-.2.334-.4.317-.4.389-.463.606-.537a1.049,1.049,0,0,1,.841.076c.215.111.684.4.987.613a14.508,14.508,0,0,1,1.367,1.113.887.887,0,0,1,.1.923,4.325,4.325,0,0,1-1.1,1.371,1.558,1.558,0,0,1-.941.388,1.365,1.365,0,0,1-.737-.154,15.508,15.508,0,0,1-6.677-5.135,14.532,14.532,0,0,1-2.075-3.768c-.276-.759-.289-1.089-.063-1.478a4.343,4.343,0,0,1,.818-.8,2.823,2.823,0,0,1,.923-.553,1.069,1.069,0,0,1,.489.045Zm4.605,1.2a3.782,3.782,0,0,1,2.708,1.619,3.879,3.879,0,0,1,.622,1.73,3.231,3.231,0,0,1,0,.727.444.444,0,0,1-.178.193.436.436,0,0,1-.328-.011c-.151-.076-.2-.2-.2-.525a3.132,3.132,0,0,0-.358-1.453,2.969,2.969,0,0,0-1.091-1.134,3.729,3.729,0,0,0-1.5-.451.5.5,0,0,1-.37-.139.355.355,0,0,1-.029-.441C608-804.6,608.153-804.625,608.62-804.555Zm.416,1.474a1.867,1.867,0,0,1,.933.465,1.931,1.931,0,0,1,.581,1.21c.053.347.031.484-.092.6a.379.379,0,0,1-.458.01c-.094-.071-.123-.145-.145-.346a1.852,1.852,0,0,0-.152-.629,1.108,1.108,0,0,0-.987-.623c-.241-.029-.313-.056-.391-.149a.363.363,0,0,1,.11-.546c.074-.037.105-.041.27-.032A2.6,2.6,0,0,1,609.036-803.081Z"
                  transform="translate(-598 810)"
                  fill="#7f4da0"
                  fillRule="evenodd"
                ></path>
              </svg>
              +39 375 604 1897
            </div>
          </div>

          <a
            className="navbar-cta-btn"
            href="https://wa.me/393756041897"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("navbar.contactUs")}
          </a>

          <div className="navbar-social">
            <a
              href="https://www.facebook.com/stradafacendobycigliantours/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <span className="material-icons">facebook</span>
            </a>
            <a
              href="https://www.instagram.com/stradafacendobycigliantours/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
          </div>

          {isAuthenticated ? (
            <button
              className="navbar-auth-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <span className="auth-icon material-icons">logout</span>
            </button>
          ) : (
            <button
              className="navbar-auth-btn navbar-login-btn"
              onClick={() => navigate("/login")}
              title="Login"
            >
              <span className="auth-icon material-icons">login</span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
